// SPDX-License-Identifier: AGPL-3.0-or-later

import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import styles from '@app/features/channel/components/GifPicker.module.css';
import {pickBestPreviewFormat} from '@app/features/channel/components/pickers/gif/FavoriteGifTypes';
import {GifPickerGridItem} from '@app/features/channel/components/pickers/gif/GifPickerGridItem';
import type {GifPickerState} from '@app/features/channel/components/pickers/gif/GifPickerState';
import type {GifPickerGridItemData} from '@app/features/channel/components/pickers/gif/GifPickerTypes';
import {computeMasonryColumns} from '@app/features/channel/components/pickers/shared/ComputeColumns';
import {MasonryVirtualGrid} from '@app/features/channel/components/pickers/shared/MasonryVirtualGrid';
import * as ExpressionPickerCommands from '@app/features/emoji/commands/ExpressionPickerCommands';
import * as GifCommands from '@app/features/expressions/commands/GifCommands';
import FavoriteGif from '@app/features/expressions/state/FavoriteGif';
import FavoriteMemes from '@app/features/expressions/state/FavoriteMemes';
import * as GifSlugUtils from '@app/features/expressions/utils/GifSlugUtils';
import QuickSwitcher from '@app/features/search/state/QuickSwitcher';
import {useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import {useCallback, useMemo} from 'react';

const CATEGORY_TILE_WIDTH = 200;
const CATEGORY_TILE_HEIGHT = 96;

const DEFAULT_GIF_SIZE = 200;
function buildSkeletonItems(count: number): Array<GifPickerGridItemData> {
	return Array.from({length: count}, (_, i) => ({
		type: 'skeleton',
		key: `skeleton-${i}`,
		width: 200,
		height: 200,
	}));
}
export const GifPickerGrid = observer(
	({
		store,
		onClose,
		autoSendKlipyGifs,
		gifAutoPlay,
		viewportWidth,
		viewportHeight,
		scrollTop,
	}: {
		store: GifPickerState;
		onClose?: () => void;
		autoSendKlipyGifs: boolean;
		gifAutoPlay: boolean;
		viewportWidth: number;
		viewportHeight: number;
		scrollTop: number;
	}) => {
		const {t} = useLingui();
		const itemGutter = 8;
		const columns = computeMasonryColumns(viewportWidth, itemGutter, {minColumns: 2});
		const favoriteMemes = FavoriteMemes.memes;
		const favoriteMemesVersion = favoriteMemes.length;
		const favoriteGifs = FavoriteGif.favoriteGifs;
		const favoriteGifsVersion = favoriteGifs.length;
		const useSavedMediaForGifFavorites = FavoriteGif.saveGifFavoritesAsSavedMedia;
		const data: Array<GifPickerGridItemData> = useMemo(() => {
			if (store.isShowingFavorites) {
				const provider = RuntimeConfig.gifProvider;
				return favoriteGifs.map((entry) => {
					const best = pickBestPreviewFormat(entry.media);
					const previewSrc = best?.format.src ?? entry.proxy_url;
					const previewProxySrc = best?.format.proxy_src ?? entry.proxy_url;
					const previewWidth = best?.format.width ?? entry.width;
					const previewHeight = best?.format.height ?? entry.height;
					const slug = GifSlugUtils.extractSlugFromUrl(provider, entry.url) ?? '';
					return {
						type: 'gif' as const,
						key: entry.url,
						gif: {
							id: slug || entry.url,
							slug,
							provider,
							title: 'GIF',
							url: entry.url,
							src: previewSrc,
							proxy_src: previewProxySrc,
							width: previewWidth > 0 ? previewWidth : DEFAULT_GIF_SIZE,
							height: previewHeight > 0 ? previewHeight : DEFAULT_GIF_SIZE,
							media: entry.media,
							favoriteGifLookup: {url: entry.url},
						},
					};
				});
			}
			if (store.isShowingFeatured) {
				const gifvMemes = favoriteMemes.filter(
					(meme) => meme.contentType.includes('gif') || meme.contentType.startsWith('video/'),
				);
				const favoriteTilePreview =
					useSavedMediaForGifFavorites && favoriteGifs.length === 0
						? ((gifvMemes.length > 0 ? gifvMemes[Math.floor(Math.random() * gifvMemes.length)]?.url : undefined) ?? '')
						: favoriteGifs[0]?.proxy_url || favoriteGifs[0]?.url || '';
				const items: Array<GifPickerGridItemData> = [
					{
						type: 'category',
						categoryKind: 'favorites',
						key: 'favorites',
						id: 'favorites',
						title: t`Favorites`,
						previewUrl: favoriteTilePreview,
						previewProxySrc: favoriteTilePreview,
						width: CATEGORY_TILE_WIDTH,
						height: CATEGORY_TILE_HEIGHT,
					},
					{
						type: 'category',
						categoryKind: 'trending',
						key: 'trending',
						id: 'trending',
						title: t`Trending GIFs`,
						previewUrl: store.featured.gifs[0]?.src ?? store.featured.gifs[0]?.url ?? '',
						previewProxySrc: store.featured.gifs[0]?.proxy_src ?? store.featured.gifs[0]?.src ?? '',
						width: CATEGORY_TILE_WIDTH,
						height: CATEGORY_TILE_HEIGHT,
					},
					...store.featured.categories.map((category) => ({
						type: 'category' as const,
						categoryKind: 'category' as const,
						key: category.name,
						id: category.name,
						title: category.name,
						previewUrl: category.gif?.src ?? category.src,
						previewProxySrc: category.gif?.proxy_src ?? category.proxy_src ?? category.src,
						width: CATEGORY_TILE_WIDTH,
						height: CATEGORY_TILE_HEIGHT,
					})),
				];
				return items;
			}
			if (store.loading && store.gifsToRender.length === 0) {
				return buildSkeletonItems(Math.max(columns * 3, 12));
			}
			return store.gifsToRender.map((gif) => ({
				type: 'gif' as const,
				key: gif.id ?? gif.src,
				gif,
			}));
		}, [
			store.isShowingFavorites,
			store.isShowingFeatured,
			store.loading,
			store.gifsToRender,
			store.featured,
			columns,
			favoriteMemesVersion,
			favoriteMemes,
			favoriteGifsVersion,
			favoriteGifs,
			useSavedMediaForGifFavorites,
			t,
		]);
		const itemKeys = useMemo(() => data.filter((item) => item.type !== 'skeleton').map((item) => item.key), [data]);
		const handleShowFavorites = useCallback(() => {
			if (FavoriteGif.saveGifFavoritesAsSavedMedia && FavoriteGif.favoriteGifs.length === 0) {
				ExpressionPickerCommands.setTab('memes');
			} else {
				store.goToFavorites();
			}
		}, [store]);
		const handleSelectByKey = useCallback(
			(itemKey: string) => {
				const item = data.find((i) => i.key === itemKey);
				if (!item || item.type === 'skeleton') return;
				if (item.type === 'category') {
					if (item.id === 'favorites') {
						handleShowFavorites();
					} else if (item.id === 'trending') {
						store.goToTrending();
					} else {
						store.setSearchTerm(item.id);
					}
					return;
				}
				const {gif} = item;
				const shareId = GifSlugUtils.resolveShareId(RuntimeConfig.gifProvider, gif);
				if (!shareId) return;
				GifCommands.registerShare(shareId, store.searchTerm);
			},
			[data, handleShowFavorites, store],
		);
		const suggestionsHeight = store.suggestions.length > 0 ? 60 : 0;
		return (
			<MasonryVirtualGrid
				data={data}
				itemKeys={itemKeys}
				columns={columns}
				itemGutter={itemGutter}
				viewportWidth={viewportWidth}
				viewportHeight={viewportHeight}
				scrollTop={scrollTop}
				checkSuspension={() => QuickSwitcher.isOpen}
				onSelectItemKey={(key) => {
					handleSelectByKey(key);
				}}
				getItemKey={(item) => item.key}
				getItemHeight={(item, _index, columnWidth) => {
					if (item.type === 'gif') {
						const g = item.gif;
						return columnWidth * (g.height / g.width);
					}
					return columnWidth * (item.height / item.width);
				}}
				extraSections={[
					{
						sectionIndex: 1,
						height: suggestionsHeight,
						render: () =>
							store.suggestions.length > 0 ? (
								<div style={{padding: '10px'}}>
									<div className={styles.suggestionsContainer}>
										{store.suggestions.map((suggestion) => (
											<button
												key={suggestion}
												type="button"
												className={styles.suggestionTag}
												onClick={() => store.setSearchTerm(suggestion)}
											>
												{suggestion}
											</button>
										))}
									</div>
								</div>
							) : null,
					},
				]}
				renderItem={({item, itemKey, coords, isFocused}) => (
					<GifPickerGridItem
						key={itemKey}
						item={item}
						itemKey={itemKey}
						coords={coords}
						isFocused={isFocused}
						onClose={onClose}
						autoSendKlipyGifs={autoSendKlipyGifs}
						gifAutoPlay={gifAutoPlay}
						searchTerm={store.searchTerm}
						onShowFavorites={handleShowFavorites}
						onShowTrending={store.goToTrending}
						onSearchCategory={store.setSearchTerm}
					/>
				)}
			/>
		);
	},
);
